/**
 * Mastodon API client - Accounts endpoints
 */

import { get, post, buildQuery } from "./http";
import type {
	Account,
	Relationship,
	Status,
	AccountStatusesParams,
	PaginationParams,
} from "./types";

/**
 * Get an account by ID
 */
export async function getAccount(id: string): Promise<Account> {
	return get<Account>(`/api/v1/accounts/${id}`);
}

/**
 * Get the current user's account
 */
export async function getCurrentAccount(): Promise<Account> {
	return get<Account>("/api/v1/accounts/verify_credentials");
}

/**
 * Get an account's followers
 */
export async function getFollowers(
	id: string,
	params?: PaginationParams,
): Promise<Account[]> {
	const query = params ? buildQuery(params) : "";
	return get<Account[]>(`/api/v1/accounts/${id}/followers${query}`);
}

/**
 * Get accounts that an account follows
 */
export async function getFollowing(
	id: string,
	params?: PaginationParams,
): Promise<Account[]> {
	const query = params ? buildQuery(params) : "";
	return get<Account[]>(`/api/v1/accounts/${id}/following${query}`);
}

/**
 * Get an account's statuses
 */
export async function getAccountStatuses(
	id: string,
	params?: AccountStatusesParams,
): Promise<Status[]> {
	const query = params ? buildQuery(params) : "";
	return get<Status[]>(`/api/v1/accounts/${id}/statuses${query}`);
}

/**
 * Get relationship between current user and given accounts
 */
export async function getRelationships(ids: string[]): Promise<Relationship[]> {
	const query = buildQuery({ id: ids });
	return get<Relationship[]>(`/api/v1/accounts/relationships${query}`);
}

/**
 * Get relationship with a single account
 */
export async function getRelationship(id: string): Promise<Relationship> {
	const relationships = await getRelationships([id]);
	return relationships[0];
}

/**
 * Follow an account
 */
export async function follow(
	id: string,
	options?: {
		reblogs?: boolean;
		notify?: boolean;
		languages?: string[];
	},
): Promise<Relationship> {
	return post<Relationship>(`/api/v1/accounts/${id}/follow`, options);
}

/**
 * Unfollow an account
 */
export async function unfollow(id: string): Promise<Relationship> {
	return post<Relationship>(`/api/v1/accounts/${id}/unfollow`);
}

/**
 * Block an account
 */
export async function block(id: string): Promise<Relationship> {
	return post<Relationship>(`/api/v1/accounts/${id}/block`);
}

/**
 * Unblock an account
 */
export async function unblock(id: string): Promise<Relationship> {
	return post<Relationship>(`/api/v1/accounts/${id}/unblock`);
}

/**
 * Mute an account
 */
export async function mute(
	id: string,
	options?: {
		notifications?: boolean;
		duration?: number;
	},
): Promise<Relationship> {
	return post<Relationship>(`/api/v1/accounts/${id}/mute`, options);
}

/**
 * Unmute an account
 */
export async function unmute(id: string): Promise<Relationship> {
	return post<Relationship>(`/api/v1/accounts/${id}/unmute`);
}

/**
 * Get accounts the user has muted
 */
export async function getMutes(params?: PaginationParams): Promise<Account[]> {
	const query = params ? buildQuery(params) : "";
	return get<Account[]>(`/api/v1/mutes${query}`);
}

/**
 * Get accounts the user has blocked
 */
export async function getBlocks(params?: PaginationParams): Promise<Account[]> {
	const query = params ? buildQuery(params) : "";
	return get<Account[]>(`/api/v1/blocks${query}`);
}

/**
 * Search for accounts
 */
export async function searchAccounts(
	q: string,
	options?: {
		limit?: number;
		following?: boolean;
		resolve?: boolean;
	},
): Promise<Account[]> {
	const query = buildQuery({ q, ...options });
	return get<Account[]>(`/api/v1/accounts/search${query}`);
}

/**
 * Lookup account by username@domain
 */
export async function lookupAccount(acct: string): Promise<Account> {
	const query = buildQuery({ acct });
	return get<Account>(`/api/v1/accounts/lookup${query}`);
}

export const accounts = {
	get: getAccount,
	getCurrent: getCurrentAccount,
	getFollowers,
	getFollowing,
	getStatuses: getAccountStatuses,
	getRelationships,
	getRelationship,
	follow,
	unfollow,
	block,
	unblock,
	mute,
	unmute,
	getMutes,
	getBlocks,
	search: searchAccounts,
	lookup: lookupAccount,
};
